import { forwardRef, useId, useCallback } from 'react';
import type { ChangeEvent, FocusEvent, InputHTMLAttributes } from 'react';
import './NumericParameterInput.css';

export interface NumericParameterInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'inputMode' | 'onChange' | 'onBlur' | 'value'> {
  /** Display label shown above the input */
  label: string;
  /** Unit suffix displayed after the input (e.g. 'ppm', 'dKH', 'mg/L') */
  unit?: string;
  /** Minimum acceptable value for range hint */
  min?: number;
  /** Maximum acceptable value for range hint */
  max?: number;
  /** Current string value (controlled by React Hook Form Controller) */
  value?: string;
  /** Called with the raw string value on every change */
  onChange?: (value: string) => void;
  /** Called when the input loses focus */
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  /** RHF field name */
  name?: string;
  /** Validation error message to display */
  error?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Additional hint text below the range hint */
  helperText?: string;
}

/**
 * NumericParameterInput — atom component for entering a single water-chemistry
 * parameter value.
 *
 * Uses type="text" + inputMode="decimal" so mobile browsers show a numeric
 * keyboard while still allowing full pattern validation at the form layer
 * (React Hook Form + Zod).
 *
 * Designed to be driven by a React Hook Form <Controller /> — pass the
 * Controller's `field` spread directly:
 *
 * ```tsx
 * <Controller
 *   name="alkalinity"
 *   control={control}
 *   render={({ field, fieldState }) => (
 *     <NumericParameterInput
 *       {...field}
 *       label="Alkalinity"
 *       unit="dKH"
 *       min={7}
 *       max={11}
 *       error={fieldState.error?.message}
 *     />
 *   )}
 * />
 * ```
 */
export const NumericParameterInput = forwardRef<HTMLInputElement, NumericParameterInputProps>(
  function NumericParameterInput(
    {
      label,
      unit,
      min,
      max,
      value = '',
      onChange,
      onBlur,
      name,
      error,
      disabled = false,
      helperText,
      id: idProp,
      className,
      ...rest
    },
    ref,
  ) {
    const generatedId = useId();
    const inputId = idProp ?? generatedId;
    const hintId = `${inputId}-hint`;
    const errorId = `${inputId}-error`;

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        if (!disabled) onChange?.(e.target.value);
      },
      [disabled, onChange],
    );

    const hasRange = min !== undefined && max !== undefined;
    const rangeText = hasRange ? `Range: ${min}–${max}${unit ? ` ${unit}` : ''}` : null;

    const describedByParts: string[] = [];
    if (rangeText ?? helperText) describedByParts.push(hintId);
    if (error) describedByParts.push(errorId);
    const ariaDescribedBy = describedByParts.length > 0 ? describedByParts.join(' ') : undefined;

    const rootClassName = ['numeric-parameter-input', className ?? ''].filter(Boolean).join(' ');
    const rowClassName = [
      'numeric-parameter-input__input-row',
      error ? 'numeric-parameter-input__input-row--error' : '',
      disabled ? 'numeric-parameter-input__input-row--disabled' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={rootClassName}>
        <label className="numeric-parameter-input__label" htmlFor={inputId}>
          {label}
        </label>

        <div className={rowClassName}>
          <input
            {...rest}
            ref={ref}
            id={inputId}
            name={name}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={value}
            onChange={handleChange}
            onBlur={onBlur}
            disabled={disabled}
            aria-invalid={Boolean(error)}
            aria-describedby={ariaDescribedBy}
            className="numeric-parameter-input__input"
          />
          {unit !== undefined && unit !== '' && (
            <span className="numeric-parameter-input__unit" aria-hidden="true">
              {unit}
            </span>
          )}
        </div>

        {(rangeText !== null || helperText !== undefined) && !error && (
          <p id={hintId} className="numeric-parameter-input__hint">
            {rangeText ?? helperText}
          </p>
        )}

        {error !== undefined && error !== '' && (
          <p id={errorId} className="numeric-parameter-input__error" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  },
);
